import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink, Redirect, Route, useParams } from 'react-router-dom';

import Paper from '@material-ui/core/Paper';
import Tab from '@material-ui/core/Tab';
import Tabs, { TabsProps } from '@material-ui/core/Tabs';
import { useWindowWidth } from '@react-hook/window-size';
import CompanyDetailsDialog from 'dialogs/CompanyDetailsDialog/CompanyDetailsDialog';
import moment from 'moment';
import styled from 'styled-components';

import CustomLoader from '../../components/CustomLoader/CustomLoader';
import { ReduxState } from '../../redux/types';
import Company, { EmptyCompany } from '../../types/Company';
import api from '../../utils/api';
import { downloadFile } from '../../utils/download';
import DeviationsTab from './tabs/DeviationsTab/DeviationsTab';
import GoodPracitesTab from './tabs/GoodPracticesTab/GoodPracticesTab';
import KpiTab from './tabs/KpiTab/KpiTab';
import PlasticAssessmentTab from './tabs/PlasticAssessmentTab/PlasticAssessmentTab';
import ReductionPlanTab from './tabs/ReductionPlanTab/ReductionPlanTab';

const StyledTabs = styled(Tabs)`
    & div {
        flex-wrap: wrap;
    }

    .MuiTabs-indicator {
        display: none;
    }

    .Mui-selected {
        border-top: 2px #ffffff solid;
        border-bottom: 2px #3f51b5 solid;
    }
`;

interface Props {
    companyPrefix?: boolean;
}

const PlasticAssessmentPage = ({ companyPrefix }: Props) => {
    const { id: assessmentId, companyId } = useParams<{ id: string, companyId?: string }>();

    const userInfo = useSelector((state: ReduxState) => state.user);
    const realCompanyId = companyId || userInfo.companyId.toString();

    const { t } = useTranslation();

    const isAdmin = userInfo.type === 'admin';
    const isCustomer = userInfo.type === 'customer';
    const isAuditor = userInfo.type === 'auditor';

    const [companyData, setCompanyData] = useState<Company>(EmptyCompany);
    const [assessmentData, setAssessmentData] = useState<any>({});
    const [isCompanyDialogOpen, setIsCompanyDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const windowWidth = useWindowWidth();

    const fullWidthSelector = windowWidth < 700;

    const refreshCompanyData = useCallback(() => {
        api.request('/companies/' + realCompanyId).then((companyRes) => {
            setCompanyData(companyRes);
        });
    }, [realCompanyId]);

    const getData = useCallback(() => {
        api.request('/companies/' + realCompanyId).then((companyRes) => {
            setCompanyData(companyRes);
            setIsLoading(false);
        });

        api.request('/companies/' + realCompanyId + '/plastic-assessments/' + assessmentId).then((assRes) => {
            setAssessmentData(assRes);
            setIsLoading(false);
        });
    }, [realCompanyId, assessmentId]);

    const exportWholePdf = useCallback(() => {
        api.request('/companies/' + realCompanyId + '/plastic-assessments/' + assessmentId + '/export/pdf', 'GET', null, true, 'blob').then(res => {
            downloadFile(res, 'export.pdf');
        });
    }, [realCompanyId, assessmentId]);

    useEffect(() => {
        getData();
    }, [getData]);

    const editDisabled = ((companyData?.state ?? '') !== 'prospect' && (companyData?.state ?? '') !== 'corrective_actions_management' && (companyData?.state ?? '') !== 'certified') || (assessmentData?.state ?? '') !== 'editing';

    const tabProps = useMemo(() => {
        const tabsProps: Partial<TabsProps> = windowWidth < 900 ? {
            variant: 'scrollable',
            scrollButtons: 'on'
        } : {};

        const tabsRender = (
            // @ts-ignore Material-UI bug
            <StyledTabs
                // @ts-ignore Material-UI bug
                component={Paper}
                indicatorColor='primary'
                textColor='primary'
                style={{ width: 'fit-content', height: 'fit-content' }}
                value={false}
                {...tabsProps}
            >
                <Tab label={t('pages.plastic_assessment.tabs.plastic_assessment.title')} component={NavLink} to={(companyPrefix ? ('/companies/' + companyId) : '') + '/plastic-assessments/' + assessmentId + '/pa'} exact activeClassName='Mui-selected' />
                <Tab label={t('pages.plastic_assessment.tabs.plastic_reduction_plan.title')} component={NavLink} to={(companyPrefix ? ('/companies/' + companyId) : '') + '/plastic-assessments/' + assessmentId + '/prp'} exact activeClassName='Mui-selected' />
                <Tab label={t('pages.plastic_assessment.tabs.kpi.title')} component={NavLink} to={(companyPrefix ? ('/companies/' + companyId) : '') + '/plastic-assessments/' + assessmentId + '/kpi'} exact activeClassName='Mui-selected' />
                <Tab label={t('pages.good_practices.title')} component={NavLink} to={(companyPrefix ? ('/companies/' + companyId) : '') + '/plastic-assessments/' + assessmentId + '/gp'} exact activeClassName='Mui-selected' />

                {assessmentData.hasDeviations && (
                    <Tab label={t('pages.plastic_assessment.tabs.deviations.title')} component={NavLink} to={(companyPrefix ? ('/companies/' + companyId) : '') + '/plastic-assessments/' + assessmentId + '/deviations'} exact activeClassName='Mui-selected' />
                )}
            </StyledTabs>
        );

        const tabsContainerRender = (
            <>
                {tabsRender}
                <div style={{ flex: '1' }} />
            </>
        );

        return {
            companyData,
            refreshCompanyData,
            assessmentYear: assessmentData.creationDate ? parseInt(moment(assessmentData.creationDate).format('Y'), 10) : null,
            companyId: parseInt(realCompanyId, 10),
            assessmentId: parseInt(assessmentId, 10),
            tabsRender: tabsContainerRender,
            isAdmin,
            isCustomer,
            isAuditor,
            fullWidthSelector,
            exportWholePdf,
            setIsCompanyDialogOpen
        };
    }, [companyId, refreshCompanyData, assessmentData.creationDate, assessmentData.hasDeviations, realCompanyId, companyPrefix, assessmentId, isAdmin, isCustomer, isAuditor, fullWidthSelector, exportWholePdf, companyData, windowWidth, t]);

    if (isLoading) {
        return <CustomLoader />;
    }

    return (
        <>
            <Route path={(companyPrefix ? ('/companies/' + companyId) : '') + '/plastic-assessments/:id'} exact render={() => {
                return <Redirect to={(companyPrefix ? ('/companies/' + companyId) : '') + '/plastic-assessments/' + assessmentId + '/pa'} />;
            }} />

            <Route path={(companyPrefix ? ('/companies/' + companyId) : '') + '/plastic-assessments/:id/pa'} exact render={() => {
                return <PlasticAssessmentTab {...tabProps} />;
            }} />

            <Route path={(companyPrefix ? ('/companies/' + companyId) : '') + '/plastic-assessments/:id/prp'} exact render={() => {
                return <ReductionPlanTab {...tabProps} />;
            }} />

            <Route path={(companyPrefix ? ('/companies/' + companyId) : '') + '/plastic-assessments/:id/kpi'} exact render={() => {
                return <KpiTab {...tabProps} editDisabled={editDisabled} firstDataMonthLimit={assessmentData?.firstDataMonthLimit} />;
            }} />

            <Route path={(companyPrefix ? ('/companies/' + companyId) : '') + '/plastic-assessments/:id/gp'} exact render={() => {
                return <GoodPracitesTab {...tabProps} />;
            }} />

            <Route path={(companyPrefix ? ('/companies/' + companyId) : '') + '/plastic-assessments/:id/deviations'} exact render={() => {
                return <DeviationsTab {...tabProps} />;
            }} />

            <CompanyDetailsDialog open={isCompanyDialogOpen} onClose={() => setIsCompanyDialogOpen(false)} companyId={parseInt(companyId ?? '0', 10)} isNewCompany={false} />
        </>
    );
};

export default memo(PlasticAssessmentPage);
